import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { Datepicker } from "./Datepicker";
import { formatLocalDate } from "utilities/dateAndTime";

export const Input = ({
    type = 'text',
    title,
    name,
    value,
    onChange,
    onBlur,
    disabled,
    placeholder,
    min = null,
    max = null,
    step = null,
    prefix = null,
    sufix = null,
    helptext = null,
    width = 'auto',
    error = false,
    warning = false,
    ...restProps
}) => {

    const ref = useRef(null);
    const dateContainerRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if(!el) return
    
        const clickHandler = (e) => {
            if(!e.target) return;

            if(!el.contains(e.target)) setIsFocused(false);
        }

        document.addEventListener("click", clickHandler);

        return () => document.removeEventListener("click", clickHandler);
    }, []);

    useEffect(() => {
        const el = dateContainerRef.current;
        if(!el) return;


        if(!isFocused)
        {
            el.style.height = el.scrollHeight + 'px';
            // eslint-disable-next-line
            const x = el.offsetHeight;
            el.style.height = '0px';
            el.style.opacity = 0;
        }
        else
        {
            el.style.height = '0px';
            // eslint-disable-next-line
            const x = el.offsetHeight;
            el.style.height = el.scrollHeight + 'px';
            el.style.opacity = 1;
            const handleTransitionEnd = () => {
                el.style.height = 'auto';
                el.removeEventListener('transitionend', handleTransitionEnd);
            };

            el.addEventListener('transitionend', handleTransitionEnd);
        }
    }, [isFocused])

    return (
        <StyledInput
            ref={ref}
            className={error ? 'error' : warning ? 'warning' : null}
            {...restProps}
        >
            {title ? <span className="inputTitle">{title}</span> : null}
            <div className="inputContainer">
                {
                    prefix ? 
                    <div className="inputPrefix">
                        {prefix}
                    </div>
                    : null
                }
                <input
                    onFocus={() => setIsFocused(true)}
                    onBlur={(e) => {onBlur?.(e)}}
                    name={name}
                    value={type === 'date' ? (value ? value?.toISOString()?.slice(0, 10) : value) : value}
                    readOnly={type === 'date'}
                    type={type}
                    disabled={disabled}
                    onChange={type === 'date' ? null : (e) => onChange?.(e)}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step ? step : 1}
                    
                />
                {
                    sufix ? 
                    <div className="inputSufix">
                        {sufix}
                    </div>
                    : null
                }
            </div>
            {helptext ? <span className="inputHelptext">{helptext}</span> : null}
            
            {
                type === 'date' ? 
                <div ref={dateContainerRef} className="datepickerContainer">
                    <Datepicker
                        date={value}
                        onChange={(e) => {onChange(formatLocalDate(e))}}
                    />
                </div>
                : null
            }
            
        </StyledInput>
    )   
}

const StyledInput = styled.label`
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacing.sm};

    &.error
    {
        span.inputTitle
        {
            color: ${({theme}) => theme.colors.errorForeground};
        }

        > div.inputContainer
        {
            border: 1px solid ${({theme}) => theme.colors.errorForeground};
            
            input, div.inputPrefix, div.inputSufix
            {
                color: ${({theme}) => theme.colors.errorForeground};
                
                .icon
                {
                    color: ${({theme}) => theme.colors.errorForeground};
                }
            }

            &:has(input:focus), &:hover
            {
                border: 1px solid ${({theme}) => theme.colors.errorForeground};

                div.inputPrefix, div.inputSufix
                {
                    color: ${({theme}) => theme.colors.errorForeground};

                    .icon
                    {
                        color: ${({theme}) => theme.colors.errorForeground};
                    }
                }
            }

            
        }
    }

    &.warning
    {
        span.inputTitle
        {
            color: ${({theme}) => theme.colors.warningForeground};
        }

        > div.inputContainer
        {
            border: 1px solid ${({theme}) => theme.colors.warningForeground};
            
            input, div.inputPrefix, div.inputSufix
            {
                color: ${({theme}) => theme.colors.warningForeground};
                
                .icon
                {
                    color: ${({theme}) => theme.colors.warningForeground};
                }
            }

            &:has(input:focus), &:hover
            {
                border: 1px solid ${({theme}) => theme.colors.warningForeground};

                div.inputPrefix, div.inputSufix
                {
                    color: ${({theme}) => theme.colors.warningForeground};
                    
                    .icon
                    {
                        color: ${({theme}) => theme.colors.warningForeground};
                    }
                }
            }

            
        }
    }

    span.inputTitle
    {
        font-size: ${({theme}) => theme.fontSize.md};
        font-weight: 500;
        color: ${({theme}) => theme.colors.neutralPrimaryForeground};
        font-size: ${({theme}) => theme.fontSize.sm};
        padding-left: ${({theme}) => theme.spacing.md};
    }

    span.inputHelptext
    {
        font-size: ${({theme}) => theme.fontSize.sm};
        color: ${({theme}) => theme.colors.mutedForeground};
        padding-left: ${({theme}) => theme.spacing.md};
        padding-right: ${({theme}) => theme.spacing.md};
        
    }
    
    > div.inputContainer
    {
        display: flex;
        height: 32px;
        align-items: center;
        border: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        border-radius: ${({theme}) => theme.cornerRadius.md};
        background: ${({theme}) => theme.colors.neutralInputBackground};
        overflow: hidden;
        transition-duration: 220ms;
        padding: 0 ${({theme}) => theme.spacing.md};

        &:has(div.inputPrefix)
        {
            padding: 0;
        }

        div.inputPrefix
        {
            min-width: 50px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            color: ${({theme}) => theme.colors.mutedForeground};

            .icon
            {
                transition-duration: 220ms;
                color: ${({theme}) => theme.colors.mutedForeground};
                font-size: ${({theme}) => theme.iconSize.sm};
            }
        }

        div.inputSufix
        {
            min-width: 40px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: right;
            padding: 0 ${({theme}) => theme.spacing.md};

            color: ${({theme}) => theme.colors.mutedForeground};
        }

        input, input:disabled
        {
            flex: 1 1 auto;
            height: 100%;
            border: none;
            outline: 0;
            min-width: 0;
            background: none;
            font-size: ${({theme}) => theme.fontSize.md};
            font-weight: 500;


        }

        input[type="date"]::-webkit-inner-spin-button,
        input[type="date"]::-webkit-calendar-picker-indicator {
            display: none;
            -webkit-appearance: none;
        }

        &:hover
        {
            border: 1px solid ${({theme}) => theme.colors.mutedForeground};

            .icon
            {
                color: ${({theme}) => theme.colors.mutedForeground};
            }
        }

        &:has(input:focus)
        {
            border: 1px solid ${({theme}) => theme.colors.primaryForeground};
            
            div.inputPrefix, div.inputSufix
            {
                color: ${({theme}) => theme.colors.neutralSecondaryForeground};

                .icon
                {
                    color: ${({theme}) => theme.colors.neutralSecondaryForeground};
                }
            }
        }
    }

    div.datepickerContainer
    {
        transition-duration: 220ms;
        overflow: hidden;
    }

    &:has(input:disabled)
    {
        opacity: 0.75;
        span.inputTitle
        {
            color: ${({theme}) => theme.colors.mutedForeground};
        }
        > div.inputContainer
        {
            background: ${({theme}) => theme.colors.mutedBackground};
        }

        > div.inputContainer:hover
        {
            border: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        }
    }
`
